import React, { useState, useEffect } from 'react';
import AdminLayout from './AdminLayout';
import Toast from './Toast';
import { getSections, updateSection, createSection, deleteSection } from '../../services/api';
import { triggerDataRefresh } from '../../context/SiteDataContext';
import './admin.css';

const PricingEditor = () => {
  const [bookPricingItems, setBookPricingItems] = useState([]);
  const [pricingConfig, setPricingConfig] = useState({
    id: 'main-pricing',
    title: 'Livre vendu sur Amazon',
    subtitle: 'Prix de vente',
  });
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({});
  const [editingConfig, setEditingConfig] = useState(false);
  const [configForm, setConfigForm] = useState({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const response = await getSections(true);
      setBookPricingItems(response.bookPricingItems || []);
      if (response.pricingConfig) {
        setPricingConfig(response.pricingConfig);
      }
    } catch (err) {
      setToast({ message: err.message, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    try {
      await updateSection(pricingConfig.id || 'main-pricing', 'pricingConfig', configForm);
      setPricingConfig({ ...pricingConfig, ...configForm });
      setEditingConfig(false);
      setToast({ message: '✓ Titre mis à jour !', type: 'success' });
      triggerDataRefresh();
    } catch (err) {
      setToast({ message: err.message, type: 'error' });
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setFormData({ ...item });
  };

  const handleCancel = () => {
    setEditingId(null);
    setFormData({});
  };

  const handleSave = async () => {
    try {
      await updateSection(editingId, 'bookPricingItem', formData);
      setToast({ message: '✓ Prix mis à jour !', type: 'success' });
      await loadData();
      setEditingId(null);
      triggerDataRefresh();
    } catch (err) {
      setToast({ message: err.message, type: 'error' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce bloc prix ?')) return;
    try {
      await deleteSection(id, 'bookPricingItem');
      setToast({ message: '✓ Bloc supprimé !', type: 'success' });
      setBookPricingItems(bookPricingItems.filter(i => i.id !== id));
      triggerDataRefresh();
    } catch (err) {
      setToast({ message: err.message, type: 'error' });
    }
  };

  const handleAddNew = async () => {
    try {
      await createSection('bookPricingItem', {
        bookTitle: 'Nouveau Livre',
        ebookPrice: '0€',
        ebookLink: '',
        brochePrice: '0€',
        brocheLink: '',
        btnText: 'Acheter',
        isActive: true,
      });
      setToast({ message: '✓ Nouveau bloc ajouté !', type: 'success' });
      await loadData();
      triggerDataRefresh();
    } catch (err) {
      setToast({ message: err.message, type: 'error' });
    }
  };

  return (
    <AdminLayout>
      <div className="admin-page">
        <header className="admin-page-header">
          <div>
            <h1>Prix par Livre</h1>
            <p>Gérez les tarifs E-Book et Livre broché pour chaque livre</p>
          </div>
          <button onClick={handleAddNew} className="admin-btn admin-btn-primary">
            + Nouveau bloc prix
          </button>
        </header>

        {/* Section Title Configuration */}
        <div className="admin-card" style={{ marginBottom: '24px' }}>
          <div className="admin-card-header" style={{ borderBottom: '1px solid #eee', paddingBottom: '12px', marginBottom: '12px' }}>
            <h3 style={{ margin: 0 }}>🏷️ Titre de la section</h3>
            {!editingConfig && (
              <button 
                onClick={() => { setEditingConfig(true); setConfigForm({ ...pricingConfig }); }}
                className="admin-btn admin-btn-sm"
              >
                Modifier
              </button>
            )}
          </div>
          {editingConfig ? (
            <div className="admin-card-edit">
              <div className="admin-form-group">
                <label>Sous-titre (petit texte)</label>
                <input
                  type="text"
                  value={configForm.subtitle || ''}
                  onChange={(e) => setConfigForm({ ...configForm, subtitle: e.target.value })}
                  placeholder="PRIX DE VENTE"
                />
              </div>
              <div className="admin-form-group">
                <label>Titre principal</label>
                <input
                  type="text"
                  value={configForm.title || ''}
                  onChange={(e) => setConfigForm({ ...configForm, title: e.target.value })}
                  placeholder="Livre vendu sur Amazon"
                />
              </div>
              <div className="admin-card-actions">
                <button onClick={() => setEditingConfig(false)} className="admin-btn">Annuler</button>
                <button onClick={handleSaveConfig} className="admin-btn admin-btn-primary">Enregistrer</button>
              </div>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '12px 0' }}>
              <span style={{ color: '#999', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '2px' }}>{pricingConfig.subtitle}</span>
              <h4 style={{ margin: '8px 0 0', fontSize: '18px' }}>{pricingConfig.title}</h4>
            </div>
          )}
        </div>

        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}

        {loading ? (
          <div className="admin-loading"><div className="spinner"></div></div>
        ) : bookPricingItems.length === 0 ? (
          <div className="admin-empty-state">
            <p>Aucun bloc prix configuré.</p>
            <button onClick={handleAddNew} className="admin-btn admin-btn-primary">
              Créer le premier bloc
            </button>
          </div>
        ) : (
          <div className="admin-cards-grid pricing-grid">
            {bookPricingItems.map((item) => (
              <div key={item.id} className="admin-card book-pricing-card">
                {editingId === item.id ? (
                  <div className="admin-card-edit">
                    <div className="admin-form-group">
                      <label>Titre du livre</label>
                      <input
                        type="text"
                        value={formData.bookTitle || ''}
                        onChange={(e) => setFormData({ ...formData, bookTitle: e.target.value })}
                        placeholder="Le dimanche à Dakar"
                      />
                    </div>
                    
                    <div className="admin-pricing-format">
                      <h4 style={{ margin: '16px 0 8px', color: '#666' }}>📱 E-Book</h4>
                      <div className="admin-form-row">
                        <div className="admin-form-group">
                          <label>Prix E-Book</label>
                          <input
                            type="text"
                            value={formData.ebookPrice || ''}
                            onChange={(e) => setFormData({ ...formData, ebookPrice: e.target.value })}
                            placeholder="5,90€"
                          />
                        </div>
                        <div className="admin-form-group">
                          <label>Lien E-Book</label>
                          <input
                            type="url"
                            value={formData.ebookLink || ''}
                            onChange={(e) => setFormData({ ...formData, ebookLink: e.target.value })}
                            placeholder="https://amzn.eu/..."
                          />
                        </div>
                      </div>
                    </div>

                    <div className="admin-pricing-format">
                      <h4 style={{ margin: '16px 0 8px', color: '#666' }}>📖 Livre broché</h4>
                      <div className="admin-form-row">
                        <div className="admin-form-group">
                          <label>Prix Broché</label>
                          <input
                            type="text"
                            value={formData.brochePrice || ''}
                            onChange={(e) => setFormData({ ...formData, brochePrice: e.target.value })}
                            placeholder="16,00€"
                          />
                        </div>
                        <div className="admin-form-group">
                          <label>Lien Broché</label>
                          <input
                            type="url"
                            value={formData.brocheLink || ''}
                            onChange={(e) => setFormData({ ...formData, brocheLink: e.target.value })}
                            placeholder="https://amzn.eu/..."
                          />
                        </div>
                      </div>
                    </div>

                    <div className="admin-form-group">
                      <label>Texte du bouton</label>
                      <input
                        type="text"
                        value={formData.btnText || ''}
                        onChange={(e) => setFormData({ ...formData, btnText: e.target.value })}
                        placeholder="Acheter"
                      />
                    </div>

                    <div className="admin-card-actions">
                      <button onClick={handleCancel} className="admin-btn">Annuler</button>
                      <button onClick={handleSave} className="admin-btn admin-btn-primary">Enregistrer</button>
                    </div>
                  </div>
                ) : (
                  <div className="book-pricing-preview">
                    <h3 className="book-pricing-title">{item.bookTitle}</h3>
                    
                    <div className="book-pricing-formats">
                      <div className="book-pricing-format">
                        <span className="format-icon">📱</span>
                        <span className="format-label">E-Book</span>
                        <span className="format-price">{item.ebookPrice}</span>
                      </div>
                      <div className="book-pricing-format">
                        <span className="format-icon">📖</span>
                        <span className="format-label">Livre broché</span>
                        <span className="format-price">{item.brochePrice}</span>
                      </div>
                    </div>

                    <div className="book-pricing-btn-preview">
                      <span className="btn-preview">{item.btnText}</span>
                    </div>

                    <div className="admin-card-actions">
                      <button onClick={() => handleEdit(item)} className="admin-btn admin-btn-sm">Modifier</button>
                      <button onClick={() => handleDelete(item.id)} className="admin-btn admin-btn-sm admin-btn-danger">Supprimer</button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default PricingEditor;
